import { Tooltip } from 'antd';
import { duration } from 'moment';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  SubmissionHoursConfiguration,
  SubmissionHoursConstrainType,
} from 'api/timesheets/configuration/submission-hours';
import { ReactComponent as ApproveOutline16 } from 'assets/svgs/approve-outline.svg';
import { Button } from 'components/atoms/button';
import { getTotalMinutesFromEntries } from 'components/organisms/timesheets/common/utils';
import { getTranslationKeyForConstraintType } from 'components/pages/timesheet-settings/submission-hours';
import { unreachable } from 'utils/unreachable';
import { TimesheetEntry } from '../types'; /// workaround for tooltip not showing in disabled button

/// workaround for tooltip not showing in disabled button
/// https://github.com/ant-design/ant-design/issues/9581
const AntdButtonIdentifier = '__ANT_BUTTON';

Button[AntdButtonIdentifier] = true;

interface Props {
  timesheetEntries: TimesheetEntry[];
  submitTimesheet: () => void;
  timesheetStatusChangeInProgress: boolean;
  submissionHoursConfiguration: SubmissionHoursConfiguration;
}
const submissionHoursConstraintMeet = (
  submissionHoursConfiguration: SubmissionHoursConfiguration,
  totalMinutesFromTimesheet: number,
): boolean => {
  switch (submissionHoursConfiguration.constraintType) {
    case SubmissionHoursConstrainType.IsEmpty:
      return true;
    case SubmissionHoursConstrainType.LessThan:
      return (
        totalMinutesFromTimesheet <=
        duration(submissionHoursConfiguration.value, 'hours').asMinutes()
      );
    case SubmissionHoursConstrainType.GreaterThan:
      return (
        totalMinutesFromTimesheet >=
        duration(submissionHoursConfiguration.value, 'hours').asMinutes()
      );
    case SubmissionHoursConstrainType.Equals:
      return (
        totalMinutesFromTimesheet ===
        duration(submissionHoursConfiguration.value, 'hours').asMinutes()
      );
    default:
      return unreachable(submissionHoursConfiguration);
  }
};

export const SubmitTimesheetButton: FC<Props> = ({
  timesheetEntries,
  submitTimesheet,
  timesheetStatusChangeInProgress,
  submissionHoursConfiguration,
}) => {
  const totalMinutesFromEntries = useMemo(
    () => getTotalMinutesFromEntries(timesheetEntries),
    [timesheetEntries],
  );
  const disableSubmit =
    totalMinutesFromEntries === 0 ||
    !submissionHoursConstraintMeet(
      submissionHoursConfiguration,
      totalMinutesFromEntries,
    );

  return (
    <Tooltip
      title={
        disableSubmit ? (
          <FormattedMessage
            id={`settings.timesheets.controls.submission_hours.constrain.${getTranslationKeyForConstraintType(
              submissionHoursConfiguration.constraintType,
            )}.message`}
            values={{
              hours:
                submissionHoursConfiguration.constraintType !==
                SubmissionHoursConstrainType.IsEmpty
                  ? submissionHoursConfiguration.value
                  : 0,
            }}
          />
        ) : null
      }
    >
      <Button
        data-cy="timesheets.buttons.submit_for_approval"
        icon={<ApproveOutline16 />}
        type="primary"
        iconPlacement="right"
        disabled={disableSubmit}
        onClick={submitTimesheet}
        loading={timesheetStatusChangeInProgress}
      >
        <FormattedMessage id="timesheets.buttons.submit_for_approval" />
      </Button>
    </Tooltip>
  );
};
